import React, { createContext, useContext, useState, useEffect, useCallback } from "react";

const CurrencyContext = createContext();

export function CurrencyProvider({ children }) {
  const [currency, setCurrency] = useState("EUR");
  const [currencySymbol, setCurrencySymbol] = useState("€");
  const [loading, setLoading] = useState(true);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchCurrency = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_URL}/api/settings/user-currency`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setCurrency(data.currency);
        setCurrencySymbol(data.currency_symbol);
      }
    } catch (error) {
      console.error("Error fetching currency:", error);
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  // Fetch on mount
  useEffect(() => {
    fetchCurrency();
  }, [fetchCurrency]);

  // Also poll for token availability after initial load
  useEffect(() => {
    const checkToken = setInterval(() => {
      const token = localStorage.getItem("token");
      if (token && currencySymbol === "€") {
        fetchCurrency();
        clearInterval(checkToken);
      }
    }, 500);
    
    // Clear after 10 seconds
    setTimeout(() => clearInterval(checkToken), 10000);
    
    return () => clearInterval(checkToken);
  }, [fetchCurrency, currencySymbol]);

  const updateCurrency = async (newCurrency) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/settings/user-currency`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ currency: newCurrency }),
      });

      if (response.ok) {
        const data = await response.json();
        setCurrency(data.currency);
        setCurrencySymbol(data.currency_symbol);
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error updating currency:", error);
      return false;
    }
  };

  const formatPrice = useCallback((amount) => {
    if (amount === null || amount === undefined) return "-";
    return `${currencySymbol}${Number(amount).toLocaleString()}`;
  }, [currencySymbol]);

  return (
    <CurrencyContext.Provider
      value={{
        currency,
        currencySymbol,
        loading,
        updateCurrency,
        formatPrice,
        refetchCurrency: fetchCurrency,
      }}
    >
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error("useCurrency must be used within a CurrencyProvider");
  }
  return context;
}
